import * as React from 'react';
import { MainLoggedInLayout } from '../../components/layouts/main.loggedIn.layout';
import { Avatar, Box, Button, Card, CircularProgress, InputLabel, Switch, TextField, Typography, styled } from '@mui/material';
import { Android, Apple, Lock, PhotoCamera, Public } from '@mui/icons-material';
import { UserContext } from '../../context/UserContext';
import { getSingleAccount, updateAvatar, updateSingleAccount } from "../../controllers/accounts/accounts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const SectionLabel = ({ label, color = '#732982' }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Box sx={{ width: 4, height: 18, borderRadius: 2, background: color, flexShrink: 0 }} />
        <Typography sx={{ fontWeight: 800, fontSize: 14, color: '#23232b', letterSpacing: 0.2 }}>{label}</Typography>
    </Box>
)

const IOS_APP_URL = 'https://apps.apple.com/us/app/make-gay-friends/id6478235888'
const ANDROID_APP_URL = 'https://play.google.com/store/apps/details?id=com.wizcomputer.makegayfriends&hl=en_US'

const SectionCard = ({ children, sx }) => (
    <Card sx={{ p: 3, mb: 2.5, borderRadius: 3, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', ...sx }}>
        {children}
    </Card>
)

export function AccountEdit() {
    const [newAvatar, setNewAvatar] = React.useState('')
    const { userDetails } = React.useContext(UserContext)
    let token = userDetails?.token ?? ''
    let id = userDetails?.id
    const queryClient = useQueryClient()
    const { data:account, refetch } = useQuery({ queryKey: ['getSingleAccount', id], queryFn: () => getSingleAccount(id, token), enabled: !!id })

    const [username, setUsername] = React.useState('')
    const [displayName, setDisplayName] = React.useState('')
    const [bio, setBio] = React.useState('')
    // Defaults to public until we know the account's real value — matches this
    // app's "opt-in to private, not opt-in to being seen" stance elsewhere.
    const [visibility, setVisibility] = React.useState(true)
    const [dirty, setDirty] = React.useState(false)

    React.useEffect(() => {
      refetch()
      if(account?.account?.avatar && !newAvatar) {
        setNewAvatar(account.account.avatar)
      }
      if (typeof account?.account?.discoverable === 'boolean') {
        setVisibility(account.account.discoverable)
      }
    }, [account])

    const updateAccount = () => {
        const body = { discoverable: visibility }
        if (username) body.username = username
        if (displayName) body.display_name = displayName
        if (bio) body.note = bio
        mutation.mutate({ body, token })
    }

    const mutation = useMutation({
        mutationFn: updateSingleAccount,
        onSuccess: () => {
        // Invalidate and refetch
        queryClient.invalidateQueries({ queryKey: ['getSingleAccount', id] })
        setDirty(false)
        refetch()
        },
    })

    const mutation2 = useMutation({
        mutationFn: updateAvatar,
        onSuccess: (data) => {
        // Invalidate and refetch
        queryClient.invalidateQueries({ queryKey: ['getSingleAccount', id] })
        setNewAvatar(data.avatar)

        // Update UserContext and localStorage with new avatar
        const stored_user = localStorage.getItem('user')
        const user_obj = stored_user ? JSON.parse(stored_user) : {}
        const updatedUser = { ...user_obj, avatar: data.avatar }
        localStorage.setItem('user', JSON.stringify(updatedUser))

        refetch()
        },
    })

    const updateAccountAvatar = (image) => {
        mutation2.mutate({
            image,
            token
        })
    }

  return (
    <MainLoggedInLayout>
        <Box sx={{ maxWidth: 640 }}>
            <Typography variant="h5" fontWeight={800} sx={{
                mb: 3,
                background: 'linear-gradient(90deg,#E40303,#732982)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
            }}>
                Settings
            </Typography>

            <SectionCard>
                <SectionLabel label="Profile Photo" color="#008026" />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                    <Box
                        component="label"
                        sx={{
                            position: 'relative',
                            width: 96,
                            height: 96,
                            flexShrink: 0,
                            display: 'inline-block',
                            cursor: 'pointer',
                            '&:hover .avatar-hover-overlay': { opacity: 1 },
                        }}
                    >
                        {/* Pride-gradient ring, matching the header/CTA gradient used elsewhere */}
                        <Box sx={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            p: '3px',
                            background: 'linear-gradient(135deg,#E40303,#FF8C00,#FFED00,#008026,#24408E,#732982)',
                        }}>
                            <Avatar
                                sx={{ width: '100%', height: '100%', border: '3px solid #fff' }}
                                alt={account?.account?.username}
                                src={newAvatar || account?.account?.avatar}
                            />
                        </Box>

                        {/* Dim + camera icon on hover (desktop) */}
                        <Box
                            className="avatar-hover-overlay"
                            sx={{
                                position: 'absolute',
                                top: 3, left: 3, right: 3, bottom: 3,
                                borderRadius: '50%',
                                background: 'rgba(0,0,0,0.45)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: mutation2.isPending ? 1 : 0,
                                transition: 'opacity 0.15s ease',
                                pointerEvents: 'none',
                            }}
                        >
                            {mutation2.isPending
                                ? <CircularProgress size={22} sx={{ color: '#fff' }} />
                                : <PhotoCamera sx={{ color: '#fff', fontSize: 22 }} />}
                        </Box>

                        {/* Always-visible badge so the affordance is clear on touch too */}
                        <Box sx={{
                            position: 'absolute',
                            bottom: -2, right: -2,
                            width: 30, height: 30,
                            borderRadius: '50%',
                            background: '#732982',
                            border: '2.5px solid #fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                        }}>
                            <PhotoCamera sx={{ color: '#fff', fontSize: 15 }} />
                        </Box>

                        <VisuallyHiddenInput type="file" accept="image/*" onChange={(value) => updateAccountAvatar(value.target.files[0])} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#23232b' }}>
                            {mutation2.isPending ? 'Uploading…' : 'Update your photo'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
                            Click your avatar to choose a new one. Square images work best.
                        </Typography>
                    </Box>
                </Box>
            </SectionCard>

            <SectionCard>
                <SectionLabel label="Profile Visibility" color={visibility ? '#008026' : '#8A95A3'} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {visibility ? <Public sx={{ color: '#008026' }} /> : <Lock sx={{ color: '#8A95A3' }} />}
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                                {visibility ? 'Public' : 'Private'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
                                {visibility
                                    ? 'Your profile is listed and discoverable by others.'
                                    : "Your profile is hidden from directories and search."}
                            </Typography>
                        </Box>
                    </Box>
                    <Switch
                        checked={visibility}
                        onChange={(e) => { setVisibility(e.target.checked); setDirty(true) }}
                    />
                </Box>
            </SectionCard>

            <SectionCard>
                <SectionLabel label="Profile Info" color="#24408E" />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Box>
                        <InputLabel sx={{ mb: 0.75, fontSize: 13, fontWeight: 600, color: '#8A95A3' }}>Username</InputLabel>
                        <TextField
                            value={username}
                            onChange={(e) => { setUsername(e.target.value); setDirty(true) }}
                            fullWidth
                            placeholder={account?.account?.username}
                            variant="outlined"
                            size="small"
                        />
                    </Box>
                    <Box>
                        <InputLabel sx={{ mb: 0.75, fontSize: 13, fontWeight: 600, color: '#8A95A3' }}>Display Name</InputLabel>
                        <TextField
                            value={displayName}
                            onChange={(e) => { setDisplayName(e.target.value); setDirty(true) }}
                            fullWidth
                            placeholder={account?.account?.display_name}
                            variant="outlined"
                            size="small"
                        />
                    </Box>

                    <Box>
                        <InputLabel sx={{ mb: 0.75, fontSize: 13, fontWeight: 600, color: '#8A95A3' }}>Bio</InputLabel>
                        <TextField
                            value={bio}
                            onChange={(e) => { setBio(e.target.value); setDirty(true) }}
                            fullWidth
                            multiline
                            rows={4}
                            placeholder={account?.account?.note?.replaceAll(/(<([^>]+)>)/ig, '').replaceAll('&#39;', "'")}
                            variant="outlined"
                        />
                    </Box>

                    <Button
                        onClick={updateAccount}
                        disabled={!dirty || mutation.isPending}
                        variant="contained"
                        sx={{ alignSelf: 'flex-start', borderRadius: 50, textTransform: 'none', fontWeight: 700, background: '#732982', '&:hover': { background: '#5c1f68' } }}
                    >
                        {mutation.isPending ? 'Saving…' : 'Update Account'}
                    </Button>
                </Box>
            </SectionCard>

            <SectionCard>
                <SectionLabel label="Get the App" color="#732982" />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Get the full MakeGayFriends experience on your phone.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                        href={IOS_APP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        startIcon={<Apple />}
                        sx={{ borderRadius: 50, textTransform: 'none', fontWeight: 700 }}
                    >
                        Download on iOS
                    </Button>
                    <Button
                        href={ANDROID_APP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        startIcon={<Android sx={{ color: '#3DDC84' }} />}
                        sx={{ borderRadius: 50, textTransform: 'none', fontWeight: 700 }}
                    >
                        Get it on Android
                    </Button>
                </Box>
            </SectionCard>

            <SectionCard sx={{ border: '1px solid #fecaca' }}>
                <SectionLabel label="Danger Zone" color="#EF4444" />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Deleting your account is permanent and cannot be undone.
                </Typography>
                <Button
                    href="https://makegayfriends.com/settings/delete"
                    color="error"
                    variant="outlined"
                    sx={{ borderRadius: 50, textTransform: 'none', fontWeight: 700 }}
                >
                    Delete Account
                </Button>
            </SectionCard>
        </Box>
    </MainLoggedInLayout>
  );
}
